import { useEffect, useState } from 'react';
import { ArrowRight, ShieldCheck, Sparkles, LineChart, Globe } from 'lucide-react';
import { copy, type Locale } from '@/copy';
import { PlanPlayground } from '@/components/PlanPlayground';

const APP_URL = import.meta.env.VITE_APP_URL ?? 'https://app.fitfive.app';

export function App() {
  const [locale, setLocale] = useState<Locale>(() => {
    const fromStorage = typeof window === 'undefined' ? null : (localStorage.getItem('fitfive-locale') as Locale | null);
    if (fromStorage && (fromStorage === 'pl' || fromStorage === 'en' || fromStorage === 'de')) {
      return fromStorage;
    }
    const browser = (typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'pl') as Locale;
    return ['pl', 'en', 'de'].includes(browser) ? browser : 'pl';
  });

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem('fitfive-locale', locale);
  }, [locale]);

  const t = copy[locale];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background/95 px-4 backdrop-blur">
        <a href="/" className="flex items-center gap-2 font-semibold">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground">F</span>
          fitfive
        </a>
        <div className="flex items-center gap-2">
          <LocaleToggle locale={locale} setLocale={setLocale} />
          <a
            href={`${APP_URL}/login`}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t.login}
          </a>
          <a
            href={`mailto:hello@fitfive.app?subject=Request%20access`}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t.cta_signup}
          </a>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-4 py-20 text-center">
        <span className="inline-block rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          {t.disclaimer}
        </span>
        <h1 className="mt-6 text-balance text-5xl font-bold tracking-tight md:text-6xl">{t.headline}</h1>
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t.subhead}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="#playground"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t.cta_try_now} <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={`mailto:hello@fitfive.app?subject=Request%20access`}
            className="inline-flex items-center rounded-md border border-border bg-card px-6 py-3 font-medium hover:bg-muted"
          >
            {t.cta_signup}
          </a>
        </div>
      </section>

      <PlanPlayground locale={locale} />

      <section className="mx-auto grid max-w-5xl gap-6 px-4 py-16 md:grid-cols-3">
        <Feature icon={Sparkles} title={t.f1_title} body={t.f1_body} />
        <Feature icon={ShieldCheck} title={t.f2_title} body={t.f2_body} />
        <Feature icon={LineChart} title={t.f3_title} body={t.f3_body} />
      </section>

      <section id="pricing" className="mx-auto max-w-5xl px-4 py-16">
        <h2 className="text-center text-3xl font-bold">{t.pricing}</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <PricingCard name={t.tier_starter} price={t.price_starter} features={t.features_starter} cta={t.cta_signup} />
          <PricingCard
            name={t.tier_pro}
            price={t.price_pro}
            features={t.features_pro}
            cta={t.cta_signup}
            highlighted
          />
          <PricingCard name={t.tier_studio} price={t.price_studio} features={t.features_studio} cta={t.cta_signup} />
        </div>
      </section>

      <footer className="border-t border-border px-4 py-8 text-center text-sm text-muted-foreground">
        <p>
          fitfive · EU-hosted (Frankfurt) · GDPR ·{' '}
          <a href="/privacy" className="hover:text-foreground">
            {t.privacy}
          </a>{' '}
          ·{' '}
          <a href="/terms" className="hover:text-foreground">
            {t.terms}
          </a>
        </p>
        <p className="mt-2">© 2026 fitfive</p>
      </footer>
    </div>
  );
}

function LocaleToggle({ locale, setLocale }: { locale: Locale; setLocale: (l: Locale) => void }) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-border bg-card px-1 py-0.5 text-xs">
      <Globe className="ml-1 h-3 w-3 text-muted-foreground" />
      {(['pl', 'en', 'de'] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLocale(l)}
          className={
            'rounded px-1.5 py-0.5 uppercase ' +
            (locale === l ? 'bg-muted text-foreground' : 'text-muted-foreground')
          }
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function Feature({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Sparkles;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function PricingCard({
  name,
  price,
  features,
  cta,
  highlighted,
}: {
  name: string;
  price: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}) {
  return (
    <div
      className={
        'rounded-lg border bg-card p-6 ' +
        (highlighted ? 'border-primary shadow-lg shadow-primary/10' : 'border-border')
      }
    >
      <h3 className="font-semibold">{name}</h3>
      <p className="mt-2 text-2xl font-bold">{price}</p>
      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
        {features.map((f) => (
          <li key={f}>· {f}</li>
        ))}
      </ul>
      <a
        href={`mailto:hello@fitfive.app?subject=Request%20access`}
        className={
          'mt-6 inline-block w-full rounded-md px-4 py-2 text-center text-sm font-medium ' +
          (highlighted
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border border-border hover:bg-muted')
        }
      >
        {cta}
      </a>
    </div>
  );
}
